import { bunja } from 'bunja';
import { atom } from 'jotai';

import { T } from '@flipster/i18n';
import { overlayItemBunja } from '@flipster/overlay';
import { SubAccountIdScope } from '@flipster/r230522/normal-sub/sub-account';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';

import { orderAttributionAtom } from '../../shared/order-attribution';
import { SymbolScope } from '../../shared/symbol/bunja';
import * as tpslActions from '../../shared/trade-actions/tpsl/state';
import { tradeModeBunja } from '../../trade-mode/state';
import { PositionSlotScope, SelectedTpslOrderScope, TpslTypeScope, tpslModalBunja } from '../state';
import { tpslModalInputBunja } from './state';
import {
  buildTpslExecutionFieldsBase,
  resolveTpslRequest,
  toPrefixedExecutionFields,
  type PrefixedExecutionFields,
  type TpslExecutionFieldsBase,
} from './tpsl-request';

export const tpslModalApiBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const symbol = bunja.use(SymbolScope);
  const tpslType = bunja.use(TpslTypeScope);
  const slot = bunja.use(PositionSlotScope);
  const selectedTpslOrder = bunja.use(SelectedTpslOrderScope);
  const accountId = bunja.use(SubAccountIdScope);
  const { tradeModeAtom } = bunja.use(tradeModeBunja);
  const { close } = bunja.use(overlayItemBunja);
  const {
    positionType,
    parentId,
    tpslTabAtom,
    tpslOrderSideAtom,
    triggerDirectionAtom,
    hasTpslOrderAtom,
    canLimitTpslAtom,
  } = bunja.use(tpslModalBunja);
  const {
    triggerPriceInputIsEmptyAtom,
    triggerPriceInputValueAtom,
    // triggerPriceTypeAtom, // ROLLBACK(last-price)
    tpslOrderQtyAtom,
    partialQtyAtom,
    isLimitTpslOrderAtom,
    isBboAtom,
    limitPriceInputValueAtom,
    persistBasisModesOnConfirm,
  } = bunja.use(tpslModalInputBunja);

  /**
   * Resolves the current (orderType, limitPrice, priceMatchStrategy) triple
   * that should be sent on the request. BBO is expressed as `Limit` + a
   * priceMatchStrategy — never as its own execution type — so the shape mirrors
   * how regular orders handle BBO.
   *
   * `limitPrice` and `priceMatchStrategy` are mutually exclusive: the unused one
   * is sent as `null` so the server preserves the intended slot on amend.
   *
   * - Environments that force Market-only, e.g. copy-trading open positions
   *   (`canLimitTpslAtom === false`), always send Market.
   * - When the "Limit Order" checkbox is off, explicit `Market` is sent. This
   *   matches the create-path default and keeps the retail-api amend behavior
   *   from silently downgrading.
   *
   * Prefixed (`orderTp*` / `orderSl*`) and unprefixed (`orderType`,
   * `limitPrice`, `priceMatchStrategy`) field-shapes are both derived from the
   * same base triple — pick based on which endpoint you're calling.
   */
  const executionFieldsBaseAtom = atom<TpslExecutionFieldsBase>((get) =>
    buildTpslExecutionFieldsBase({
      canLimitTpsl: get(canLimitTpslAtom),
      isLimitTpslOrder: get(isLimitTpslOrderAtom),
      isBbo: get(isBboAtom),
      limitPrice: String(get(limitPriceInputValueAtom)),
    }),
  );

  /** Prefixed fields (`orderTp*` / `orderSl*`) for Multi-position + amend paths. */
  const prefixedExecutionFieldsAtom = atom<PrefixedExecutionFields>((get) =>
    toPrefixedExecutionFields(get(executionFieldsBaseAtom), tpslType),
  );

  function getOrderQtyForApi(tpslTab: 'entire' | 'partial'): string | undefined {
    switch (positionType) {
      case 'open': {
        // API에서 Entire인 경우에는 undefined로 보낸다 (partial 100%랑 구분하기 위함)
        if (tpslTab === 'entire') return undefined;
        return store.get(partialQtyAtom);
      }
      case 'pending':
      default:
        return store.get(tpslOrderQtyAtom);
    }
  }

  async function confirmTpsl(): Promise<void> {
    try {
      const updated = await updateTpsl();
      if (updated) {
        // Persist basis modes only after successful Confirm.
        persistBasisModesOnConfirm();
        snackbar.success({ message: <T id='Position.r250401.Position_update_toast' /> });
      }
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
      const errorCode = getErrorCode(error);
      const closableErrorCodes = ['UnknownPosition', 'InvalidTriggerPriceRatio'];
      if (closableErrorCodes.includes(errorCode)) close();
    }
  }

  async function updateTpsl(): Promise<boolean> {
    const isEmptyTriggerPriceInput = store.get(triggerPriceInputIsEmptyAtom);
    // delete → create 과정에서 tab이 'entire'로 리셋될 수 있으므로, delete 전에 tab과 qty를 캡처.
    // partialQtyAtom은 atomWithDefault라서, 유저가 qty를 수정하지 않았으면 set된 적 없음 →
    // delete 후 tpslTabAtom default가 'entire'로 재평가되면서 partialQtyAtom도 positionQty로 재계산됨.
    const tpslTab = store.get(tpslTabAtom);
    const orderQty = getOrderQtyForApi(tpslTab);

    const request = resolveTpslRequest({
      positionType,
      hasSelectedOrder: !!selectedTpslOrder,
      isEmptyTriggerInput: isEmptyTriggerPriceInput,
    });

    switch (request) {
      // 빈 trigger로 업데이트 → 기존 tpsl order 삭제
      case 'delete':
        return deleteTpsl();
      // 값 변경 업데이트 → 기존 삭제 후 재생성(서버가 update를 delete→create로 처리)
      case 'replace':
        await deleteTpsl();
        return upsertTpsl(orderQty);
      // 신규 생성(add) 또는 pending 업서트
      case 'upsert':
      default:
        return upsertTpsl(orderQty);
    }
  }

  async function upsertTpsl(orderQty: string | undefined): Promise<boolean> {
    if (positionType === 'open') await upsertOpenPositionTpsl(orderQty);
    else if (positionType === 'pending') await upsertPendingPositionTpsl(orderQty);
    else throw new Error('Unexpected position type');
    return true;
  }

  async function deleteTpsl(): Promise<boolean> {
    const hasTpslOrder = store.get(hasTpslOrderAtom);
    if (hasTpslOrder === 'empty') return false;
    if (positionType === 'open') await deleteOpenPositionTpsl();
    else if (positionType === 'pending') await deletePendingPositionTpsl();
    else throw new Error('Unexpected position type');
    return true;
  }

  function upsertOpenPositionTpsl(orderQty: string | undefined) {
    const tradeMode = store.get(tradeModeAtom);
    if (tradeMode === 'MultiplePositions') return upsertOpenPositionTpslMultiplePositions(orderQty);
    if (tradeMode === 'OneWay') return upsertOpenPositionTpslOneWay(orderQty);
    throw new Error('Unexpected trade mode');
  }

  // 이하 leaf 함수들은 atom/scope 값을 읽어 trade-actions/tpsl 원시 함수로 위임한다 (페이로드는 그쪽에서 조립).
  function upsertOpenPositionTpslMultiplePositions(orderQty: string | undefined) {
    return tpslActions.upsertOpenPositionTpslMultiplePositions({
      symbol,
      slot,
      side: store.get(tpslOrderSideAtom),
      triggerPrice: String(store.get(triggerPriceInputValueAtom)),
      triggerDirection: store.get(triggerDirectionAtom),
      // triggerPriceType: store.get(triggerPriceTypeAtom), // ROLLBACK(last-price)
      orderQty,
      executionFields: store.get(prefixedExecutionFieldsAtom),
      attribution: store.get(orderAttributionAtom),
      accountId,
    });
  }

  function upsertOpenPositionTpslOneWay(orderQty: string | undefined) {
    return tpslActions.upsertOpenPositionTpslOneWay({
      symbol,
      side: store.get(tpslOrderSideAtom),
      orderQty,
      triggerPrice: String(store.get(triggerPriceInputValueAtom)),
      triggerDirection: store.get(triggerDirectionAtom),
      // triggerPriceType: store.get(triggerPriceTypeAtom), // ROLLBACK(last-price)
      executionBase: store.get(executionFieldsBaseAtom),
      attribution: store.get(orderAttributionAtom),
      accountId,
    });
  }

  function upsertPendingPositionTpsl(orderQty: string | undefined) {
    return tpslActions.upsertPendingPositionTpsl({
      symbol,
      parentId,
      type: tpslType === 'tp' ? 'OrderTp' : 'OrderSl',
      triggerPrice: String(store.get(triggerPriceInputValueAtom)),
      triggerDirection: store.get(triggerDirectionAtom),
      // triggerPriceType: store.get(triggerPriceTypeAtom), // ROLLBACK(last-price)
      orderQty,
      executionFields: store.get(prefixedExecutionFieldsAtom),
      attribution: store.get(orderAttributionAtom),
      accountId,
    });
  }

  function deleteOpenPositionTpsl() {
    if (!selectedTpslOrder) throw new Error('selectedTpslOrder is not initialized');
    return tpslActions.deleteOpenPositionTpsl({
      symbol,
      orderId: selectedTpslOrder.orderId,
      attribution: store.get(orderAttributionAtom),
      accountId,
    });
  }

  function deletePendingPositionTpsl() {
    return tpslActions.deletePendingPositionTpsl({
      symbol,
      parentId,
      type: tpslType === 'tp' ? 'OrderTp' : 'OrderSl',
      attribution: store.get(orderAttributionAtom),
      accountId,
    });
  }

  return { confirmTpsl };
});

tpslModalApiBunja.debugLabel = 'tpslModalApiBunja';
